import type { SelectProps } from "@radix-ui/react-select";
import * as SelectPrimitive from "@radix-ui/react-select";
import { IconCircle } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { SelectTriggerLabel } from "@/components/select-trigger-label";
import { Select, SelectContent, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

type RadioSelectProps = SelectProps & {
  value: string;
  options: { label: string; value: string }[];
  label: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

type RadioSelectItemProps = {
  label: string | ReactNode;
  value: string;
  className?: string;
  optionValue: string;
  disabled?: boolean;
};

export function RadioSelectItem({
  label,
  value,
  className,
  optionValue,
  disabled,
}: RadioSelectItemProps) {
  return (
    <SelectPrimitive.Item
      className={cn(
        "relative flex cursor-default select-none items-center p-4 outline-none",
        "cursor-pointer rounded-md data-[highlighted]:bg-brand-green-300/10",
        optionValue === value && "font-medium",
        className,
      )}
      disabled={disabled}
      value={optionValue}
    >
      <span
        className={cn(
          "mr-2 flex size-5 items-center justify-center rounded-full border",
          "bg-brand-white",
          optionValue === value && "border-brand-green-300 bg-brand-green-300",
        )}
      >
        <SelectPrimitive.ItemIndicator>
          <IconCircle
            className="h-3 w-3 fill-current text-brand-white"
            stroke={1.25}
          />
        </SelectPrimitive.ItemIndicator>
      </span>

      <SelectPrimitive.ItemText>{label}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export const RadioSelect = ({
  value,
  options,
  label,
  placeholder,
  disabled,
  className,
  onValueChange,
  ...props
}: RadioSelectProps) => {
  return (
    <Select
      disabled={disabled}
      onValueChange={onValueChange}
      value={value}
      {...props}
    >
      <SelectTriggerLabel className={cn(className)} label={label}>
        <SelectValue placeholder={placeholder} />
      </SelectTriggerLabel>
      <SelectContent>
        {options.map((option) => (
          <RadioSelectItem
            key={option.value}
            label={option.label}
            optionValue={option.value}
            value={value}
          />
        ))}
      </SelectContent>
    </Select>
  );
};
